<template>
  <div class="overview-panel pt-3 px-lg-2 px-2">
    <!-- Welcome Section -->
    <div class="welcome-section mb-4 mt-2" :class="{ 'gradient-loading': isLoading }">
      <div class="welcome-inner">
        <div class="welcome-content">
          <h2 class="welcome-title">Panel de Administración</h2>
          <p class="welcome-subtitle">
            Bienvenido al panel de administración.
          </p>
        </div>
        <div class="welcome-actions">
          <button @click="refreshData" class="btn btn-outline-primary" :disabled="isLoading">
            <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': isLoading }"></i>
            {{ isLoading ? 'Actualizando...' : 'Actualizar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Summary Cards -->
    <div class="mb-3">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3 px-3">
        <h4 class="section-title mb-0 text-white">
          <i class="fas fa-chart-pie me-2"></i>
          Tus números
        </h4>
      </div>
      <div class="period-selector d-flex align-items-center justify-content-start flex-wrap gap-2 px-3">
        <div class="d-flex flex-wrap gap-1" role="group">
          <button 
            v-for="period in periods" 
            :key="period.value"
            @click="changePeriod(period.value)"
            class="btn text-white fw-semibold"
            :class="[
              selectedPeriod === period.value && !isCustomPeriod
                ? 'btn-primary' 
                : 'btn-outline-primary'
            ]"
          >
            {{ period.label }}
          </button>
        </div>
        <button 
          @click="showDateRangePicker = !showDateRangePicker" 
          class="btn text-white fw-semibold"
          :class="[isCustomPeriod ? 'btn-primary' : 'btn-outline-primary']"
        >
          <i class="fas fa-calendar-alt me-1"></i> 
          <span class="d-none d-sm-inline">Personalizar</span>
          <span class="d-inline d-sm-none">Fechas</span>
        </button>
      </div>
    </div>
    
    <!-- Date range picker -->
    <div v-if="showDateRangePicker" class="date-range-picker mb-4">
      <div class="gradient-card">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-5 col-12">
              <label for="startDate" class="form-label">Desde</label>
              <input 
                type="date" 
                id="startDate" 
                class="form-control" 
                v-model="customStartDate"
              />
            </div>
            <div class="col-md-5 col-12">
              <label for="endDate" class="form-label">Hasta</label>
              <input 
                type="date" 
                id="endDate" 
                class="form-control" 
                v-model="customEndDate"
              />
            </div>
            <div class="col-md-2 col-12 d-flex align-items-end">
              <button 
                class="btn btn-primary w-100" 
                @click="applyCustomDateRange"
                :disabled="!isValidDateRange"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-4 px-3">
      <div class="col-12">
        <div class="row g-3 g-md-4 stats-metrics-row px-3">
          <div v-for="(stat, key) in statCards" :key="key" class="col-12 col-sm-6 col-lg-3">
            <div class="gradient-card stats-card" :class="{ 'gradient-loading': isLoading }">
              <div v-if="isLoading" class="skeleton-content">
                <div class="skeleton-icon"></div>
                <div class="skeleton-text">
                  <div class="skeleton-value"></div>
                  <div class="skeleton-label"></div>
                </div>
              </div>
              <div v-else class="stats-details">
                <div class="stats-card-content">
                  <div class="stats-icon" :style="{ backgroundColor: stat.iconBackground, color: stat.iconColor }">
                    <i :class="stat.icon"></i>
                  </div>
                  <div class="stats-copy">
                    <h4 class="stats-value text-white">
                      <span v-if="stat.isCurrency">$</span>
                      {{ formatValue(stats[stat.value], stat.isCurrency) }}
                    </h4>
                    <p class="stats-label text-white fw-normal">
                      {{ stat.label }}
                      <i class="fas fa-info-circle text-muted ms-1" 
                         data-bs-toggle="tooltip" 
                         :title="stat.tooltip"></i>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Orders Status Cards -->
    <h4 class="section-title mb-3 mt-5 pt-4 px-3">
      <i class="fas fa-tasks me-2"></i>
      <span class="d-none d-sm-inline">Gestión de Pedidos</span>
      <span class="d-inline d-sm-none">Pedidos</span>
    </h4>
    <div class="row g-4 mb-4 px-3 orders-status-row">
      <div class="col-lg-6">
        <div class="gradient-card h-100" :class="{ 'gradient-loading': isLoading }">
          <div class="card-header">
            <h5 class="text-white fw-normal"><i class="fas fa-clock me-2"></i>Pendientes</h5>
            <span class="badge bg-warning">{{ statistics.pending }}</span>
          </div>
          <div class="card-body">
            <div v-if="isLoading" class="skeleton-orders">
              <div v-for="i in 3" :key="i" class="skeleton-order-item">
                <div class="skeleton-order-info">
                  <div class="skeleton-id"></div>
                  <div class="skeleton-customer"></div>
                  <div class="skeleton-amount"></div>
                </div>
                <div class="skeleton-button"></div>
              </div>
            </div>
            <div v-else-if="pendingOrders.length === 0" class="empty-state">
              <i class="fas fa-box-open text-white"></i>
              <p>No hay pedidos pendientes</p>
            </div>
            <div v-else class="order-list">
              <div v-for="order in pendingOrders" :key="order.id" class="order-item">
                <div class="order-info">
                  <span class="order-id">#{{ order.id }}</span>
                  <span class="order-customer">{{ order.recipient_name }}</span>
                  <span class="order-amount">
                    ${{ formatValue(getVentaTotalARS(order), true) }}
                    
                  </span>
                </div>
                <button class="btn btn-sm btn-outline-primary" @click="viewOrder(order)">
                  Ver detalles
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="gradient-card h-100" :class="{ 'gradient-loading': isLoading }">
          <div class="card-header">
            <h5 class="text-white fw-normal"><i class="fas fa-check-circle me-2"></i>Pagados</h5>
            <span class="badge bg-success">{{ statistics.paid }}</span>
          </div>
          <div class="card-body">
            <div v-if="isLoading" class="skeleton-orders">
              <div v-for="i in 3" :key="i" class="skeleton-order-item">
                <div class="skeleton-order-info">
                  <div class="skeleton-id"></div>
                  <div class="skeleton-customer"></div>
                  <div class="skeleton-amount"></div>
                </div>
                <div class="skeleton-button"></div>
              </div>
            </div>
            <div v-else-if="paidOrders.length === 0" class="empty-state">
              <i class="fas fa-dollar-sign text-white"></i>
              <p>No hay pedidos pagados</p>
            </div>
            <div v-else class="order-list">
              <div v-for="order in paidOrders" :key="order.id" class="order-item">
                <div class="order-info">
                  <span class="order-id">#{{ order.id }}</span>
                  <span class="order-customer">{{ order.recipient_name }}</span>
                  <span class="order-amount">
                    ${{ formatValue(getVentaTotalARS(order), true) }}
                    
                  </span>
                </div>
                <button class="btn btn-sm btn-outline-primary" @click="viewOrder(order)">
                  Ver detalles
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="gradient-card h-100" :class="{ 'gradient-loading': isLoading }">
          <div class="card-header">
            <h5 class="text-white fw-normal"><i class="fas fa-hourglass-half me-2"></i>Por Pagar</h5>
            <span class="badge bg-info">{{ statistics.payment_pending }}</span>
          </div>
          <div class="card-body">
            <div v-if="isLoading" class="skeleton-orders">
              <div v-for="i in 3" :key="i" class="skeleton-order-item">
                <div class="skeleton-order-info">
                  <div class="skeleton-id"></div>
                  <div class="skeleton-customer"></div>
                  <div class="skeleton-amount"></div>
                </div>
                <div class="skeleton-button"></div>
              </div>
            </div>
            <div v-else-if="paymentPendingOrders.length === 0" class="empty-state">
              <i class="fas fa-money-bill-wave text-white"></i>
              <p>No hay pedidos por pagar</p>
            </div>
            <div v-else class="order-list">
              <div v-for="order in paymentPendingOrders" :key="order.id" class="order-item">
                <div class="order-info">
                  <span class="order-id">#{{ order.id }}</span>
                  <span class="order-customer">{{ order.recipient_name }}</span>
                  <span class="order-amount">
                    ${{ formatValue(getVentaTotalARS(order), true) }}
                    
                  </span>
                </div>
                <button class="btn btn-sm btn-outline-info" @click="viewOrder(order)">
                  Ver detalles
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="gradient-card h-100" :class="{ 'gradient-loading': isLoading }">
          <div class="card-header">
            <h5 class="text-white fw-normal"><i class="fas fa-times-circle me-2"></i>Cancelados</h5>
            <span class="badge bg-danger">{{ statistics.cancelled }}</span>
          </div>
          <div class="card-body">
            <div v-if="isLoading" class="skeleton-orders">
              <div v-for="i in 3" :key="i" class="skeleton-order-item">
                <div class="skeleton-order-info">
                  <div class="skeleton-id"></div>
                  <div class="skeleton-customer"></div>
                  <div class="skeleton-amount"></div>
                </div>
                <div class="skeleton-button"></div>
              </div>
            </div>
            <div v-else-if="cancelledOrders.length === 0" class="empty-state">
              <i class="fas fa-ban text-white"></i>
              <p>No hay pedidos cancelados</p>
            </div>
            <div v-else class="order-list">
              <div v-for="order in cancelledOrders" :key="order.id" class="order-item">
                <div class="order-info">
                  <span class="order-id">#{{ order.id }}</span>
                  <span class="order-customer">{{ order.recipient_name }}</span>
                  <span class="order-amount">
                    ${{ formatValue(getVentaTotalARS(order), true) }}
                    
                  </span>
                </div>
                <button class="btn btn-sm btn-outline-danger" @click="viewOrder(order)">
                  Ver detalles
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="px-3 my-4 pt-5">
      <div class="gradient-card low-stock-card" :class="{ 'gradient-loading': isLoadingLowStock }">
        <div class="low-stock-header">
          <div>
            <h5 class="low-stock-title mb-1">
              <i class="fas fa-triangle-exclamation me-2"></i>
              Productos con bajo stock
            </h5>
            <p class="low-stock-subtitle mb-0">Alertas de inventario con 10 unidades o menos.</p>
          </div>
          <router-link to="/configuracion/notificaciones" class="btn btn-outline-primary btn-sm">
            Ver todas
          </router-link>
        </div>

        <div v-if="isLoadingLowStock" class="low-stock-list">
          <div v-for="n in 4" :key="n" class="low-stock-skeleton"></div>
        </div>

        <div v-else-if="lowStockItems.length === 0" class="low-stock-empty">
          <i class="fas fa-circle-check"></i>
          <span>Sin alertas de stock por ahora.</span>
        </div>

        <div v-else class="low-stock-list">
          <article
            v-for="item in lowStockItems"
            :key="item.id"
            class="low-stock-item"
            :class="{ 'low-stock-item--danger': item.stock <= 0 }"
          >
            <img :src="item.image || fallbackLogo" :alt="item.product_name" class="low-stock-image" />
            <div class="low-stock-info">
              <div class="low-stock-name" :title="item.product_name">{{ item.product_name }}</div>
              <div class="low-stock-meta">
                <span v-if="item.variant_label">{{ item.variant_label }}</span>
                <span v-if="item.sku">SKU {{ item.sku }}</span>
              </div>
            </div>
            <span class="low-stock-badge">{{ item.stock }} u.</span>
            <router-link
              :to="{ name: 'ProductEdit', params: { productId: item.product_id } }"
              class="btn btn-sm btn-outline-primary low-stock-action"
            >
              Editar
            </router-link>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import statisticsService from '@/services/statisticsService';
import { getOrders } from '@/services/orders';
import { useToast } from 'vue-toastification';
import { getCurrentDollarRate } from '@/services/payment';
import { getLowStockNotifications } from '@/services/products';
import fallbackLogo from '@/assets/images/logo.png';

const router = useRouter();
const toast = useToast();

// Verificar si el usuario es administrador
const isAdmin = ref(localStorage.getItem('isAdmin') === 'true');

const dollarRate = ref(0);

onMounted(async () => {
  if (!isAdmin.value) {
    router.push('/configuracion/my-orders');
    return;
  }

  // Set default dates to current week
  const today = new Date();
  const firstDayOfWeek = new Date(today);
  firstDayOfWeek.setDate(today.getDate() - today.getDay());  // Start of current week (Sunday)
  customEndDate.value = today.toISOString().split('T')[0];
  customStartDate.value = firstDayOfWeek.toISOString().split('T')[0];

  // Fetch dollar rate
  try {
    const rate = await getCurrentDollarRate();
    if (rate && rate.value) {
      dollarRate.value = parseFloat(rate.value);
    }
  } catch (e) {
    dollarRate.value = 0;
  }

  // Cargar datos con el periodo semanal por defecto
  loadData();
});

const stats = ref({
  ventas: 0,
  facturacion: 0,
  ticketPromedio: 0,
  usuarios: 0,     // Usuarios registrados en el periodo seleccionado
  usuariosTotal: 0 // Total histórico de usuarios hasta la fecha final del periodo
});

const pendingOrders = ref([]);
const paidOrders = ref([]);
const paymentPendingOrders = ref([]);
const cancelledOrders = ref([]);
const lowStockItems = ref([]);

const statCards = {
  'Usuarios': {
    label: 'Usuarios registrados',
    value: 'usuarios',
    icon: 'fas fa-user-plus',
    iconBackground: 'rgba(var(--success-color-rgb), 0.2)',
    iconColor: 'var(--primary-color)',
    tooltip: 'Número total de usuarios registrados',
    isCurrency: false
  },
  'Ventas': {
    label: 'Ventas',
    value: 'ventas',
    icon: 'fas fa-shopping-cart',
    iconBackground: 'rgba(111, 66, 193, 0.2)',
    iconColor: '#6f42c1',
    tooltip: 'Número total de ventas realizadas',
    isCurrency: false
  },
  'Facturación': {
    label: 'Facturación',
    value: 'facturacion',
    icon: 'fas fa-dollar-sign',
    iconBackground: 'rgba(23, 162, 184, 0.2)',
    iconColor: '#17a2b8',
    tooltip: 'Importe total facturado',
    isCurrency: true
  },
  'Ticket promedio': {
    label: 'Ticket promedio',
    value: 'ticketPromedio',
    icon: 'fas fa-receipt',
    iconBackground: 'rgba(255, 193, 7, 0.2)',
    iconColor: '#ffc107',
    tooltip: 'Valor promedio de cada venta',
    isCurrency: true
  }
};

const formatValue = (value, isCurrency) => {
  if (!value) return '0';
  if (isCurrency) {
    return value.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  return value.toLocaleString('es-AR');
};

const viewOrder = (order) => {
  router.push(`/configuracion/pedidos/${order.id}`);
};

const isLoading = ref(false);
const isLoadingLowStock = ref(false);

// New refs
const selectedPeriod = ref('week');
const isCustomPeriod = ref(false);
const showDateRangePicker = ref(false);
const customStartDate = ref('');
const customEndDate = ref('');

// Definir los períodos disponibles
const periods = [
  { value: 'day', label: 'Hoy' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'year', label: 'Año' }
];

// Computed property to validate date range
const isValidDateRange = computed(() => {
  if (!customStartDate.value || !customEndDate.value) return false;
  
  const start = new Date(customStartDate.value);
  const end = new Date(customEndDate.value);
  
  return start <= end;
});

const changePeriod = async (period) => {
  selectedPeriod.value = period;
  isCustomPeriod.value = false;
  showDateRangePicker.value = false;
  
  // Limpiar las listas de órdenes antes de cargar los nuevos datos
  pendingOrders.value = [];
  paidOrders.value = [];
  paymentPendingOrders.value = [];
  cancelledOrders.value = [];
  
  await loadData();
};

const applyCustomDateRange = async () => {
  if (!isValidDateRange.value) return;
  
  isCustomPeriod.value = true;
  
  // Limpiar las listas de órdenes antes de cargar los nuevos datos
  pendingOrders.value = [];
  paidOrders.value = [];
  paymentPendingOrders.value = [];
  cancelledOrders.value = [];
  
  await loadData(true);
};

const loadData = async (isCustomRange = false) => {
  isLoading.value = true;
  isLoadingLowStock.value = true;
    try {
      let statsData;
      let userStatsData;
      
      // Get sales statistics
      if (isCustomRange && customStartDate.value && customEndDate.value) {
        statsData = await statisticsService.getStatistics(
          null,
          customStartDate.value,
          customEndDate.value
        );
        
        // Get user statistics with the same period
        userStatsData = await statisticsService.getUserStats(
          null,
          customStartDate.value,
          customEndDate.value
        );
      } else {
        statsData = await statisticsService.getStatistics(selectedPeriod.value);
        userStatsData = await statisticsService.getUserStats(selectedPeriod.value);
      }
      
      stats.value = {
        ...statsData.stats,
        usuarios: userStatsData.stats.period_customers || 0,
        usuariosTotal: userStatsData.stats.total_customers || 0
      };

    try {
      // En el overview, la gestión de pedidos debe mostrar siempre el estado global
      // (igual que en la lista de pedidos), no filtrado por rango de fechas.
      const response = await getOrders(true, false);
      const orders = Array.isArray(response) ? response : response.results || [];
      
      console.log(`Cargando órdenes globales para overview. Total: ${orders.length}`);
      
      // Filtrar pedidos por estado usando los estados correctos del backend
      pendingOrders.value = orders.filter(o => o.status === 'pending').slice(0, 5);
      paidOrders.value = orders.filter(o => o.status === 'paid').slice(0, 5);
      paymentPendingOrders.value = orders.filter(o => o.status === 'payment_pending').slice(0, 5);
      cancelledOrders.value = orders.filter(o => o.status === 'cancelled').slice(0, 5);

      // Actualizar estadísticas
      statistics.pending = orders.filter(o => o.status === 'pending').length;
      statistics.payment_pending = orders.filter(o => o.status === 'payment_pending').length;
      statistics.paid = orders.filter(o => o.status === 'paid').length;
      statistics.cancelled = orders.filter(o => o.status === 'cancelled').length;

      // Mostrar en consola para debug
      console.log('Órdenes globales en overview:');
      console.log('Total orders:', orders.length);
      console.log('Pending:', statistics.pending);
      console.log('Payment Pending:', statistics.payment_pending);
      console.log('Paid:', statistics.paid);
      console.log('Cancelled:', statistics.cancelled);
      
    } catch (orderError) {
      console.error('Error loading orders:', orderError);
      toast.error("Error al cargar los pedidos: " + (orderError.message || "Error desconocido"));
      // Inicializar arrays vacíos en caso de error
      pendingOrders.value = [];
      paidOrders.value = [];
      paymentPendingOrders.value = [];
      cancelledOrders.value = [];
      // Reiniciar estadísticas
      statistics.pending = 0;
      statistics.payment_pending = 0;
      statistics.paid = 0;
      statistics.cancelled = 0;
    }

    try {
      const lowStockData = await getLowStockNotifications({ threshold: 10 });
      lowStockItems.value = Array.isArray(lowStockData.items)
        ? lowStockData.items.slice(0, 5)
        : [];
    } catch (lowStockError) {
      console.error('Error loading low stock products:', lowStockError);
      lowStockItems.value = [];
    } finally {
      isLoadingLowStock.value = false;
    }
    toast.success("¡Datos actualizados correctamente!", {
      timeout: 2000
    });
  } catch (error) {
    console.error('Error loading dashboard data:', error);
    toast.error("Error al cargar los datos: " + (error.message || "Error desconocido"), {
      timeout: 3000
    });
  } finally {
    isLoading.value = false;
    isLoadingLowStock.value = false;
  }
};

const refreshData = () => {
  if (isCustomPeriod.value) {
    loadData(true);
  } else {
    loadData();
  }
};

// Inicializar estadísticas
const statistics = reactive({
  pending: 0,
  payment_pending: 0,
  paid: 0,
  cancelled: 0
});

// Helper to get total in ARS (copied from AdminSalesHistory)
function getVentaTotalARS(order) {
  if (!order.products) return order.total;
  return order.products.reduce((total, product) => {
    const currency = product.product_details?.currency || 'ARS';
    const price = product.price_at_time;
    const qty = product.quantity;
    if (currency === 'USD' && dollarRate.value) {
      return total + (price * qty * dollarRate.value);
    } else {
      return total + (price * qty);
    }
  }, 0);
}
</script>

<style scoped>
/* Overview panel */
.overview-panel {
  width: 100%;
}

/* Grid de métricas: 4 tarjetas alineadas, misma altura en la fila */
.stats-metrics-row {
  align-items: stretch;
}
.stats-metrics-row > [class*='col-'] {
  display: flex;
  flex-direction: column;
}
.stats-metrics-row .gradient-card {
  flex: 1 1 auto;
  width: 100%;
  min-height: 6.25rem;
}

/* Welcome section */
.welcome-section {
  background: var(--black-color-3, #1c1c1c);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  border-left: 3px solid var(--primary-color);
}

.welcome-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.welcome-content {
  flex: 1;
  min-width: 0;
}

.welcome-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.welcome-subtitle {
  font-size: 0.8rem;
  color: #888 !important;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.welcome-actions {
  flex-shrink: 0;
}

/* Section title */
.section-title {
  font-size: 0.92rem;
  font-weight: 700;
  color: #fff;
}

/* Period selector */
.period-selector .btn {
  padding: 0.4rem 0.85rem;
  font-size: 0.78rem;
  border-radius: 8px;
  margin-right: 0.35rem;
  transition: all 0.15s ease;
}

.period-selector .btn-outline-primary {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  color: #aaa;
}

.period-selector .btn-outline-primary:hover {
  background: rgba(255,255,255,0.1);
  color: #fff;
  border-color: rgba(255,255,255,0.15);
}

.period-selector .btn-primary {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

/* Stats cards */
.stats-details {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.stats-card {
  padding: 1rem;
  display: flex;
}

.stats-card-content {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  min-width: 0;
}

.stats-copy {
  min-width: 0;
  text-align: right;
}

.stats-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.stats-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.15rem;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.stats-label {
  margin-bottom: 0;
  font-size: 0.78rem;
  color: #888 !important;
  line-height: 1.25;
}

/* Low stock */
.low-stock-card {
  padding: 1rem;
}

.low-stock-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}

.low-stock-title {
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
}

.low-stock-title i {
  color: #fbbf24;
}

.low-stock-subtitle {
  color: #888;
  font-size: 0.78rem;
}

.low-stock-list {
  display: grid;
  gap: 0.5rem;
}

.low-stock-item {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem;
  border-radius: 8px;
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(245,158,11,0.18);
}

.low-stock-item--danger {
  border-color: rgba(239,68,68,0.24);
}

.low-stock-image {
  width: 46px;
  height: 46px;
  border-radius: 8px;
  object-fit: contain;
  background: rgba(0,0,0,0.25);
}

.low-stock-info {
  min-width: 0;
}

.low-stock-name {
  color: #eee;
  font-size: 0.86rem;
  font-weight: 650;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.low-stock-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  color: #777;
  font-size: 0.72rem;
}

.low-stock-badge {
  min-width: 52px;
  text-align: center;
  color: #fbbf24;
  background: rgba(245,158,11,0.12);
  border-radius: 999px;
  padding: 0.22rem 0.5rem;
  font-size: 0.76rem;
  font-weight: 800;
}

.low-stock-item--danger .low-stock-badge {
  color: #f87171;
  background: rgba(239,68,68,0.12);
}

.low-stock-empty {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: #9ca3af;
  padding: 1rem 0.25rem 0.25rem;
  font-size: 0.84rem;
}

.low-stock-empty i {
  color: var(--success-color);
}

.low-stock-skeleton {
  height: 58px;
  border-radius: 8px;
  background: rgba(255,255,255,0.05);
  animation: pulse 1.5s infinite;
}

/* Order section cards */
.orders-status-row {
  align-items: stretch;
}

.orders-status-row > [class*='col-'] {
  display: flex;
}

.orders-status-row > [class*='col-'] > .gradient-card {
  width: 100%;
}

.gradient-card .card-header {
  background: rgba(0,0,0,0.15);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding: 0.85rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.gradient-card .card-header h5 {
  margin: 0;
  font-size: 0.9rem;
}

.gradient-card .card-body {
  padding: 1rem;
  max-height: 280px;
  overflow-y: auto;
}

/* Order list */
.order-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0.75rem;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.04);
  border-radius: 8px;
  transition: all 0.15s ease;
}

.order-item:hover {
  border-color: rgba(var(--primary-color-rgb), 0.2);
  background: rgba(0,0,0,0.25);
}

.order-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.order-id {
  font-family: monospace;
  color: var(--primary-light, var(--primary-color));
  font-size: 0.82rem;
  font-weight: 600;
}

.order-customer {
  color: #ccc;
  font-size: 0.84rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-amount {
  color: #aaa;
  font-size: 0.84rem;
  font-weight: 600;
}

/* Order action buttons */
.order-item .btn {
  padding: 0.3rem 0.65rem;
  font-size: 0.74rem;
  border-radius: 6px;
  font-weight: 600;
}

.btn-outline-primary {
  background: rgba(var(--primary-color-rgb), 0.1);
  border: 1px solid rgba(var(--primary-color-rgb), 0.25);
  color: var(--primary-light, var(--primary-color));
  border-radius: 8px;
  padding: 0.4rem 0.85rem;
  font-size: 0.82rem;
  transition: all 0.15s ease;
}

.btn-outline-primary:hover {
  background: rgba(var(--primary-color-rgb), 0.2);
  color: #fff;
}

.btn-outline-success {
  background: rgba(var(--success-color-rgb), 0.08);
  border: 1px solid rgba(var(--success-color-rgb), 0.2);
  color: var(--success-color);
}

.btn-outline-success:hover {
  background: rgba(var(--success-color-rgb), 0.18);
  color: #fff;
}

.btn-outline-info {
  background: rgba(59,130,246,0.08);
  border: 1px solid rgba(59,130,246,0.2);
  color: #60a5fa;
}

.btn-outline-info:hover {
  background: rgba(59,130,246,0.18);
  color: #fff;
}

.btn-outline-danger {
  background: rgba(239,68,68,0.08);
  border: 1px solid rgba(239,68,68,0.2);
  color: #f87171;
}

.btn-outline-danger:hover {
  background: rgba(239,68,68,0.18);
  color: #fff;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 1.5rem 1rem;
  color: #555;
}

.empty-state i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
  color: #555 !important;
}

.empty-state p {
  font-size: 0.82rem;
  color: #555 !important;
}

/* Skeleton loading */
.skeleton-content {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.skeleton-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(255,255,255,0.05);
  animation: pulse 1.5s infinite;
}

.skeleton-text {
  flex: 1;
}

.skeleton-value {
  height: 28px;
  width: 80px;
  background: rgba(255,255,255,0.05);
  border-radius: 6px;
  margin-bottom: 0.5rem;
  margin-left: auto;
  animation: pulse 1.5s infinite;
}

.skeleton-label {
  height: 14px;
  width: 100px;
  background: rgba(255,255,255,0.04);
  border-radius: 4px;
  margin-left: auto;
  animation: pulse 1.5s infinite;
}

.skeleton-orders {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.skeleton-order-item {
  height: 48px;
  background: rgba(255,255,255,0.04);
  border-radius: 8px;
  animation: pulse 1.5s infinite;
}

/* Badge */
.badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 5px;
}

/* Date range picker */
.date-range-picker .gradient-card {
  padding: 1rem;
}

/* Loading Animation */
@keyframes loading {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 0.3; }
}

/* Responsive */
@media (max-width: 768px) {
  .welcome-section {
    padding: 0.85rem 1rem;
  }

  .stats-value {
    font-size: 1.2rem;
  }

  .order-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .order-info {
    gap: 0.5rem;
    flex-wrap: wrap;
    width: 100%;
  }

  .order-id   { font-size: 0.78rem; }
  .order-customer { font-size: 0.8rem; max-width: 140px; }
  .order-amount   { font-size: 0.8rem; }

  .order-item .btn {
    align-self: flex-end;
    font-size: 0.72rem;
    padding: 0.25rem 0.55rem;
  }

  .gradient-card .card-body { max-height: 260px; }

  .low-stock-header {
    flex-direction: column;
  }

  .low-stock-item {
    grid-template-columns: 42px minmax(0, 1fr) auto;
  }

  .low-stock-action {
    grid-column: 2 / -1;
    justify-self: start;
  }

  .period-selector {
    padding-left: 0.25rem !important;
    padding-right: 0.25rem !important;
  }
}

@media (max-width: 480px) {
  /* En pantallas muy pequeñas, el welcome apila vertical */
  .welcome-inner {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.65rem;
  }

  .welcome-title {
    font-size: 0.95rem;
    white-space: normal;
  }

  .welcome-subtitle {
    white-space: normal;
  }

  .welcome-actions {
    width: 100%;
  }

  .welcome-actions .btn {
    width: 100%;
    font-size: 0.8rem;
  }
}

@media (max-width: 576px) {
  .overview-panel {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }

  .stats-value   { font-size: 1.05rem; }
  .stats-label   { font-size: 0.72rem; }

  .period-selector .btn {
    padding: 0.3rem 0.5rem;
    font-size: 0.7rem;
  }

  .section-title { font-size: 0.85rem; padding-left: 0.75rem; }

  .gradient-card .card-header h5 { font-size: 0.82rem; }
}
</style>

